import { useState, useEffect, useCallback } from "react";
import {
  Database,
  Snowflake,
  Save,
  Loader2,
  Trash2,
  Check,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Server,
  Plug,
  Key,
  Lock,
} from "lucide-react";
import { apiv2, type DBConfigOut } from "../apiv2";

const DB_TYPES = [
  { value: "snowflake", label: "Snowflake", icon: Snowflake },
  { value: "postgres", label: "PostgreSQL", icon: Database },
  { value: "mysql", label: "MySQL", icon: Database },
  { value: "sqlserver", label: "SQL Server", icon: Database },
  { value: "oracle", label: "Oracle", icon: Database },
  { value: "bigquery", label: "BigQuery", icon: Database },
];

interface Props {
  projectId: string;
  kind: "source" | "target";
  canEdit: boolean;
}

export default function SharedDBConfig({ projectId, kind, canEdit }: Props) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState<DBConfigOut | null>(null);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  // Form state
  const [dbType, setDbType] = useState("snowflake");
  const [host, setHost] = useState("");
  const [port, setPort] = useState<number | undefined>(undefined);
  const [database, setDatabase] = useState("");
  const [schemaName, setSchemaName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [authType, setAuthType] = useState<"password" | "pat">("password");
  const [account, setAccount] = useState("");
  const [warehouse, setWarehouse] = useState("");
  const [role, setRole] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const isSnowflake = dbType === "snowflake";
  const label = kind === "source" ? "Source Database" : "Target Database";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cfg =
        kind === "source"
          ? await apiv2.getSourceDB(projectId)
          : await apiv2.getTargetDB(projectId);
      setExisting(cfg);
      if (cfg) {
        setDbType(cfg.db_type);
        setHost(cfg.host);
        setPort(cfg.port ?? undefined);
        setDatabase(cfg.database);
        setSchemaName(cfg.schema_name);
        setUsername(cfg.username);
        setAuthType((cfg.auth_type as "password" | "pat") || "password");
        setAccount(cfg.account);
        setWarehouse(cfg.warehouse);
        setRole(cfg.role);
      }
    } catch {
      // OK — no config yet
    } finally {
      setLoading(false);
    }
  }, [projectId, kind]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const data = {
        db_type: dbType,
        host,
        port,
        database,
        schema_name: schemaName,
        username,
        password: password || undefined,
        auth_type: authType,
        account,
        warehouse,
        role,
      };
      const result =
        kind === "source"
          ? await apiv2.setSourceDB(projectId, data)
          : await apiv2.setTargetDB(projectId, data);
      setExisting(result);
      setPassword("");
      setSuccess("Saved successfully");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      if (kind === "source") await apiv2.deleteSourceDB(projectId);
      else await apiv2.deleteTargetDB(projectId);
      setExisting(null);
      // Reset form
      setDbType("snowflake");
      setHost("");
      setPort(undefined);
      setDatabase("");
      setSchemaName("");
      setUsername("");
      setPassword("");
      setAuthType("password");
      setAccount("");
      setWarehouse("");
      setRole("");
      setSuccess("Removed");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to remove");
    }
  };

  return (
    <div
      className="glass rounded-2xl overflow-hidden"
      style={{
        background: "rgba(28,28,42,0.5)",
        border: "1px solid rgba(255,255,255,0.05)",
      }}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-white/[0.02] transition"
      >
        <Server
          size={16}
          className={kind === "source" ? "text-brand-400" : "text-purple-400"}
        />
        <span className="text-sm font-bold text-white flex-1">{label}</span>
        {existing && (
          <span className="text-[11px] text-green-400 bg-green-500/10 px-2 py-0.5 rounded border border-green-500/20">
            Configured
          </span>
        )}
        {open ? (
          <ChevronUp size={14} className="text-white/30" />
        ) : (
          <ChevronDown size={14} className="text-white/30" />
        )}
      </button>

      {open && (
        <div className="px-5 pb-5 pt-1 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 size={20} className="animate-spin text-white/20" />
            </div>
          ) : (
            <>
              {/* DB type selector */}
              <div className="flex flex-wrap gap-2">
                {DB_TYPES.map(({ value, label: lbl, icon: Icon }) => (
                  <button
                    key={value}
                    onClick={() => {
                      if (canEdit) setDbType(value);
                    }}
                    disabled={!canEdit}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      dbType === value
                        ? "text-white"
                        : "text-white/30 hover:text-white/50"
                    } disabled:opacity-50`}
                    style={
                      dbType === value
                        ? {
                            background:
                              kind === "source"
                                ? "linear-gradient(135deg, #86BC25, #6B9B1E)"
                                : "linear-gradient(135deg, #a855f7, #7c3aed)",
                          }
                        : {
                            background: "rgba(28,28,42,0.5)",
                            border: "1px solid rgba(255,255,255,0.06)",
                          }
                    }
                  >
                    <Icon size={12} /> {lbl}
                  </button>
                ))}
              </div>

              {/* Form fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {isSnowflake ? (
                  <>
                    <Field
                      label="Account"
                      value={account}
                      onChange={setAccount}
                      placeholder="abc12345.us-east-1"
                      disabled={!canEdit}
                    />
                    <Field
                      label="Warehouse"
                      value={warehouse}
                      onChange={setWarehouse}
                      placeholder="COMPUTE_WH"
                      disabled={!canEdit}
                    />
                    <Field
                      label="Role"
                      value={role}
                      onChange={setRole}
                      placeholder="SYSADMIN"
                      disabled={!canEdit}
                    />
                  </>
                ) : (
                  <>
                    <Field
                      label="Host"
                      value={host}
                      onChange={setHost}
                      placeholder="localhost"
                      disabled={!canEdit}
                    />
                    <Field
                      label="Port"
                      value={String(port || "")}
                      onChange={(v) => setPort(parseInt(v) || undefined)}
                      placeholder="5432"
                      disabled={!canEdit}
                    />
                  </>
                )}
                <Field
                  label="Database"
                  value={database}
                  onChange={setDatabase}
                  placeholder="my_database"
                  disabled={!canEdit}
                />
                <Field
                  label="Schema"
                  value={schemaName}
                  onChange={setSchemaName}
                  placeholder="public"
                  disabled={!canEdit}
                />
                <Field
                  label="Username"
                  value={username}
                  onChange={setUsername}
                  placeholder="user"
                  disabled={!canEdit}
                />
                <Field
                  label={
                    authType === "pat"
                      ? "Personal Access Token (PAT)"
                      : "Password"
                  }
                  value={password}
                  onChange={setPassword}
                  placeholder={
                    existing?.has_password ? "••••• (unchanged)" : "••••••••"
                  }
                  type="password"
                  disabled={!canEdit}
                />
              </div>

              {/* Auth type selector */}
              <div>
                <label className="block text-[11px] font-medium text-white/30 mb-1.5">
                  Authentication Method
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      if (canEdit) setAuthType("password");
                    }}
                    disabled={!canEdit}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      authType === "password"
                        ? "text-white"
                        : "text-white/30 hover:text-white/50"
                    } disabled:opacity-50`}
                    style={
                      authType === "password"
                        ? {
                            background:
                              "linear-gradient(135deg, #86BC25, #6B9B1E)",
                          }
                        : {
                            background: "rgba(28,28,42,0.5)",
                            border: "1px solid rgba(255,255,255,0.06)",
                          }
                    }
                  >
                    <Lock size={12} /> Password
                  </button>
                  <button
                    onClick={() => {
                      if (canEdit) setAuthType("pat");
                    }}
                    disabled={!canEdit}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      authType === "pat"
                        ? "text-white"
                        : "text-white/30 hover:text-white/50"
                    } disabled:opacity-50`}
                    style={
                      authType === "pat"
                        ? {
                            background:
                              "linear-gradient(135deg, #86BC25, #6B9B1E)",
                          }
                        : {
                            background: "rgba(28,28,42,0.5)",
                            border: "1px solid rgba(255,255,255,0.06)",
                          }
                    }
                  >
                    <Key size={12} /> Personal Access Token (PAT)
                  </button>
                </div>
                {authType === "pat" && (
                  <p className="text-[11px] text-white/20 mt-1">
                    PAT is used in place of password for Snowflake and other
                    providers that support token-based auth.
                  </p>
                )}
              </div>

              {/* Status messages */}
              {success && (
                <div className="text-xs text-green-400 flex items-center gap-1">
                  <Check size={12} /> {success}
                </div>
              )}
              {error && (
                <div className="text-xs text-red-400 flex items-center gap-1">
                  <AlertTriangle size={12} /> {error}
                </div>
              )}
              {testResult && (
                <div
                  className={`text-xs flex items-center gap-1 ${testResult.success ? "text-green-400" : "text-red-400"}`}
                >
                  {testResult.success ? (
                    <Check size={12} />
                  ) : (
                    <AlertTriangle size={12} />
                  )}
                  {testResult.message}
                </div>
              )}

              {/* Action buttons */}
              {canEdit && (
                <div className="flex items-center gap-3 pt-2">
                  <button
                    onClick={async () => {
                      setTesting(true);
                      setTestResult(null);
                      setError("");
                      try {
                        const result = await apiv2.testDBConnection(projectId, {
                          db_type: dbType,
                          host,
                          port,
                          database,
                          schema_name: schemaName,
                          username,
                          password: password || undefined,
                          auth_type: authType,
                          account,
                          warehouse,
                          role,
                        });
                        setTestResult(result);
                      } catch (err) {
                        setTestResult({
                          success: false,
                          message:
                            err instanceof Error ? err.message : "Test failed",
                        });
                      } finally {
                        setTesting(false);
                      }
                    }}
                    disabled={testing || !database || !username}
                    className="px-4 py-2 text-white rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 disabled:opacity-30 border"
                    style={{
                      background: "rgba(28,28,42,0.5)",
                      borderColor: "rgba(134,188,37,0.3)",
                    }}
                  >
                    {testing ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Plug size={14} />
                    )}
                    Test Connection
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving || !database}
                    className="px-5 py-2 text-white rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 disabled:opacity-30"
                    style={{
                      background:
                        saving || !database
                          ? "rgba(255,255,255,0.05)"
                          : kind === "source"
                            ? "linear-gradient(135deg, #86BC25, #6B9B1E)"
                            : "linear-gradient(135deg, #a855f7, #7c3aed)",
                    }}
                  >
                    {saving ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Save size={14} />
                    )}
                    Save {label}
                  </button>
                  {existing && (
                    <button
                      onClick={handleDelete}
                      className="px-3 py-2 text-red-400 rounded-lg text-xs hover:bg-red-500/10 border border-red-500/20 flex items-center gap-1.5 transition"
                    >
                      <Trash2 size={14} /> Remove
                    </button>
                  )}
                </div>
              )}

              <p className="text-[11px] text-white/15 mt-2">
                This configuration is shared with all agents in this project.
                Agents will use it instead of asking you to re-enter connection
                details.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  disabled = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  type?: string;
  disabled?: boolean;
}) {
  return (
    <div>
      <label className="block text-[11px] font-medium text-white/30 mb-1">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full px-3 py-2 rounded-lg text-xs text-white placeholder-white/15 focus:outline-none border transition disabled:opacity-40"
        style={{
          background: "rgba(14,14,24,0.6)",
          borderColor: "rgba(255,255,255,0.06)",
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = "rgba(134,188,37,0.3)";
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)";
        }}
      />
    </div>
  );
}
